export const ROUTE_PATHS = {
  HOME: '/',
  ONCO_TREINO: '/onco-treino'
} as const;

export const CHECKOUT_URL = 'https://pay.kiwify.com.br/hCr27g9';

export interface CourseModule {
  id: number;
  title: string;
  duration: string;
  topics: string[];
}

export interface FAQItem {
  question: string;
  answer: string;
}

export interface OncoTreinoModule {
  id: number;
  title: string;
  description: string;
  cases: number;
  questions: number;
  icon: string;
}

export interface PainPoint {
  id: number;
  icon: string;
  title: string;
  desc: string;
}

export const COURSE_MODULES: CourseModule[] = [
  {
    id: 1,
    title: 'Fundamentos da Oncologia e Equipe Multidisciplinar',
    duration: '2h30min',
    topics: [
      'Oncogênese e biologia tumoral',
      'Estadiamento TNM completo',
      'Tumor board multidisciplinar',
      'Terapias avançadas (CAR-T, imunoterapia)',
      'Papel da enfermagem na equipe'
    ]
  },
  {
    id: 2,
    title: 'Quimioterapia — Teoria, Protocolos e Administração Segura',
    duration: '3h15min',
    topics: [
      'Classes farmacológicas e mecanismos de ação',
      'Toxicidades agudas e tardias',
      'Os 9 certos da administração segura',
      'Extravasamento: prevenção e manejo',
      'NR-32 e biossegurança'
    ]
  },
  {
    id: 3,
    title: 'Radioterapia e Oncologia Cirúrgica na Prática',
    duration: '2h45min',
    topics: [
      'Teleterapia e técnicas modernas',
      'Braquiterapia de alta e baixa taxa',
      'Radiocirurgia estereotáxica',
      'Cirurgias preventivas e paliativas',
      'Cuidados de enfermagem específicos'
    ]
  },
  {
    id: 4,
    title: 'Cuidados Paliativos, Ética e Humanização',
    duration: '2h50min',
    topics: [
      'Conceito de dor total (física, emocional, social, espiritual)',
      'Escala analgésica da OMS',
      'Autonomia e tomada de decisão compartilhada',
      'Comunicação empática e difícil',
      'Interdisciplinaridade e suporte familiar'
    ]
  },
  {
    id: 5,
    title: 'Gestão da Qualidade e Farmacoeconomia Oncológica',
    duration: '2h20min',
    topics: [
      'Ciclo PDCA aplicado à oncologia',
      'Indicadores de qualidade e segurança',
      'Análise de custo-efetividade (QALY, ICER)',
      'Balanced Scorecard em oncologia',
      'Segurança do paciente e cultura de qualidade'
    ]
  },
  {
    id: 6,
    title: 'Prática Avançada e Integração Clínica',
    duration: '3h00min',
    topics: [
      'Casos clínicos integrados',
      'Raciocínio clínico em oncologia',
      'Protocolos institucionais',
      'Navegação do paciente oncológico',
      'Visão sistêmica do tratamento'
    ]
  }
];

export interface LearningItem {
  title: string;
  description: string;
}

export const LEARNING_ITEMS: LearningItem[] = [
  { title: 'Oncogênese e biologia tumoral', description: 'Compreenda como o câncer se desenvolve e os mecanismos moleculares envolvidos' },
  { title: 'Estadiamento TNM completo', description: 'Interprete laudos e classifique tumores com segurança e precisão clínica' },
  { title: 'Farmacologia oncológica avançada', description: 'Domine classes de drogas, mecanismos de ação e interações relevantes' },
  { title: 'Quimioterapia: classes e mecanismos', description: 'Entenda cada protocolo e como agir com segurança na administração' },
  { title: 'Toxicidades agudas e tardias', description: 'Identifique, previna e maneje os efeitos adversos mais frequentes' },
  { title: 'Os 9 certos da administração', description: 'Aplique os princípios de segurança na administração de antineoplásicos' },
  { title: 'Extravasamento: prevenção e manejo', description: 'Aprenda a identificar e agir em situações críticas como anafilaxia e extravasamento' },
  { title: 'NR-32 e biossegurança', description: 'Proteja-se e proteja sua equipe no manuseio de agentes citotóxicos' },
  { title: 'Radioterapia moderna (IMRT, radiocirurgia)', description: 'Entenda as técnicas e os cuidados de enfermagem específicos em radioterapia' },
  { title: 'Braquiterapia de alta e baixa taxa', description: 'Conheça os tipos de braquiterapia e o papel do enfermeiro no processo' },
  { title: 'Oncologia cirúrgica na prática', description: 'Prepare e acompanhe o paciente oncológico no perioperatório com segurança' },
  { title: 'Imunoterapia e CAR-T', description: 'Explore as terapias imunológicas de última geração e seus desafios assistenciais' },
  { title: 'Terapias-alvo moleculares', description: 'Compreenda os alvos terapêuticos e os cuidados com estas medicações inovadoras' },
  { title: 'Tumor board multidisciplinar', description: 'Entenda o papel da enfermagem nas discussões de caso e decisões clínicas coletivas' },
  { title: 'Cuidados paliativos avançados', description: 'Ofereça conforto, dignidade e suporte integral ao paciente em fase avançada' },
  { title: 'Conceito de dor total', description: 'Avalie e maneje a dor em todas as suas dimensões: física, emocional e espiritual' },
  { title: 'Escala analgésica da OMS', description: 'Utilize a escada analgésica para guiar o manejo da dor oncológica' },
  { title: 'Comunicação empática e difícil', description: 'Desenvolva habilidades para abordar notícias difíceis com compaixão e clareza' },
  { title: 'Autonomia e tomada de decisão', description: 'Respeite as escolhas do paciente e aplique os princípios da bioética oncológica' },
  { title: 'Suporte familiar e interdisciplinaridade', description: 'Integre família e equipe no plano de cuidados de forma humanizada' },
  { title: 'Gestão da qualidade em oncologia', description: 'Aplique ferramentas de qualidade para melhorar os processos assistenciais' },
  { title: 'Ciclo PDCA aplicado', description: 'Use o PDCA para implementar melhorias contínuas no setor de oncologia' },
  { title: 'Indicadores de qualidade e segurança', description: 'Mensure, analise e apresente indicadores assistenciais com embasamento' },
  { title: 'Farmacoeconomia oncológica', description: 'Compreenda o custo-efetividade dos tratamentos e seu impacto nas decisões' },
  { title: 'Análise de custo-efetividade (QALY, ICER)', description: 'Interprete métricas farmacoeconomicas usadas em oncologia de precisão' },
  { title: 'Balanced Scorecard em oncologia', description: 'Alinhe estratégia e operação no gerenciamento de serviços oncológicos' },
  { title: 'Casos clínicos integrados', description: 'Exercite o raciocínio clínico com casos reais desafiadores da prática oncológica' },
  { title: 'Raciocínio clínico avançado', description: 'Desenvolva pensamento crítico para tomada de decisão segura e fundamentada' },
  { title: 'Protocolos institucionais', description: 'Conheça e aplique protocolos baseados em evidências nos serviços de oncologia' },
  { title: 'Navegação do paciente oncológico', description: 'Coordene o cuidado ao longo de toda a jornada do paciente com câncer' },
];

export const FAQ_ITEMS: FAQItem[] = [
  {
    question: 'O que é o curso Oncologia em Foco?',
    answer: 'É um treinamento prático, direto e completo para enfermeiros, técnicos e estudantes que desejam compreender a oncologia com base científica, segurança assistencial e visão humanizada. O curso cobre desde fundamentos até tópicos avançados como farmacoeconomia e gestão da qualidade.'
  },
  {
    question: 'Qual a duração do curso?',
    answer: 'O curso possui 6 aulas com carga horária total de aproximadamente 16 horas de conteúdo. Você pode assistir no seu próprio ritmo, com acesso por 12 meses a partir da data de compra.'
  },
  {
    question: 'O curso oferece certificado?',
    answer: 'Sim. Ao concluir o curso, você receberá um certificado digital de conclusão que pode ser utilizado para comprovação de horas complementares e atualização profissional.'
  },
  {
    question: 'Quem pode fazer o curso?',
    answer: 'Enfermeiros, técnicos de enfermagem e estudantes de enfermagem que desejam iniciar ou aprofundar seus conhecimentos em oncologia. Não é necessário ter experiência prévia na área.'
  },
  {
    question: 'Como funciona o acesso ao curso?',
    answer: 'Após a confirmação do pagamento pela Kiwify, você receberá um e-mail com as instruções de acesso. O curso fica disponível na plataforma online por 12 meses para você assistir quando e quantas vezes quiser.'
  },
  {
    question: 'Existe garantia de reembolso?',
    answer: 'Sim. Você tem 7 dias de garantia incondicional. Se por qualquer motivo não ficar satisfeito, basta solicitar o reembolso integral dentro desse prazo.'
  },
  {
    question: 'Qual a diferença entre este curso e uma pós-graduação?',
    answer: 'Este é um curso de atualização e aperfeiçoamento profissional, com foco prático e direto nos principais tópicos da oncologia. Não substitui uma pós-graduação lato sensu, mas oferece conhecimento sólido e aplicável imediatamente na prática clínica.'
  },
  {
    question: 'Quais formas de pagamento são aceitas?',
    answer: 'Você pode pagar em até 12x no cartão de crédito ou à vista via Pix. O pagamento é processado de forma segura pela plataforma Kiwify.'
  },
  {
    question: 'Preciso ter conhecimento prévio em oncologia?',
    answer: 'Não. O curso foi desenvolvido para profissionais e estudantes que desejam iniciar ou aprofundar seus conhecimentos em oncologia, partindo dos fundamentos até tópicos avançados.'
  },
  {
    question: 'Terei suporte durante o curso?',
    answer: 'Sim. Você terá acesso a suporte técnico para questões relacionadas à plataforma e ao conteúdo do curso durante todo o período de acesso.'
  }
];

export const ONCO_TREINO_MODULES: OncoTreinoModule[] = [
  {
    id: 1,
    title: 'Fundamentos do Câncer',
    description: 'Base teórica sólida em oncogênese, estadiamento TNM e tipos de câncer',
    cases: 15,
    questions: 12,
    icon: '🧬'
  },
  {
    id: 2,
    title: 'Farmacologia Oncológica',
    description: 'Quimioterápicos, terapias-alvo e mecanismos de ação',
    cases: 18,
    questions: 15,
    icon: '💊'
  },
  {
    id: 3,
    title: 'Emergências Oncológicas',
    description: 'Situações críticas: neutropenia febril, síndrome de lise tumoral, compressão medular',
    cases: 12,
    questions: 10,
    icon: '🚨'
  },
  {
    id: 4,
    title: 'Acesso Venoso e Dispositivos',
    description: 'Técnicas de punção, manutenção de cateteres e prevenção de complicações',
    cases: 10,
    questions: 8,
    icon: '💉'
  },
  {
    id: 5,
    title: 'Manejo de Sintomas',
    description: 'Controle de náuseas, vômitos, fadiga, mucosite e outros efeitos colaterais',
    cases: 16,
    questions: 14,
    icon: '🤢'
  },
  {
    id: 6,
    title: 'Cuidados Paliativos Avançados',
    description: 'Controle da dor, comunicação empática e suporte familiar',
    cases: 14,
    questions: 12,
    icon: '💚'
  },
  {
    id: 7,
    title: 'Gestão e Protocolos',
    description: 'Indicadores de qualidade, segurança do paciente e farmacoeconomia',
    cases: 17,
    questions: 19,
    icon: '📋'
  }
];

export const PAIN_POINTS: PainPoint[] = [
  {
    id: 1,
    icon: '😰',
    title: 'Insegurança Clínica',
    desc: 'Você se sente perdido ao cuidar de pacientes oncológicos, sem saber se está fazendo o correto?'
  },
  {
    id: 2,
    icon: '📚',
    title: 'Informação Fragmentada',
    desc: 'Cansado de buscar informações espalhadas em artigos, vídeos e PDFs sem conexão entre si?'
  },
  {
    id: 3,
    icon: '⚠️',
    title: 'Medo de Errar',
    desc: 'Tem receio de cometer erros graves na administração de quimioterápicos ou manejo de intercorrências?'
  },
  {
    id: 4,
    icon: '🚫',
    title: 'Falta de Prática',
    desc: 'Não tem acesso a casos clínicos reais para treinar seu raciocínio antes de atuar na prática?'
  },
  {
    id: 5,
    icon: '💼',
    title: 'Estagnação Profissional',
    desc: 'Sente que está estagnado na carreira e precisa de um diferencial para se destacar no mercado?'
  },
  {
    id: 6,
    icon: '🎯',
    title: 'Falta de Visão Sistêmica',
    desc: 'Não compreende como a oncologia funciona de forma integrada, desde o diagnóstico até os cuidados paliativos?'
  }
];